import { faDumbbell } from "@fortawesome/free-solid-svg-icons";
import { Chart, ChartData, CustomTooltipProps, formatShortChartDate, TitleStyle, TooltipContainer, TooltipText } from "./Chart";
import { useMemo } from "react";
import { DateTime } from "luxon";
import { Guard } from "../utils";
import _ from "lodash";
import { WeightUnit, getWeightKg } from "../quiz/weight";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";

const createCustomTooltip = ({ active, payload }: CustomTooltipProps): JSX.Element => {
  if (active && payload && payload.length) {
    const displayWeight = payload[0].value as number;
    const date = DateTime.fromMillis(payload[0].payload.date);
    const showYear = date.year !== DateTime.local().year;
    return (
      <TooltipContainer>
        <TooltipText>{i18n.t("splash.charts.muscleGain.tooltip", { date: formatShortChartDate(date, showYear), amount: displayWeight.toPrecision(2) })}</TooltipText>
      </TooltipContainer>
    );
  } else {
    return <></>;
  }
};

type MuscleGainData = ChartData & {
  muscleGainedLbs: number;
};

const isMuscleGainData: Guard<MuscleGainData> = (data: unknown): data is MuscleGainData => {
  return (data as MuscleGainData).muscleGainedLbs !== undefined;
};

export function getMuscleGainPredictionData({ data, showTitle, weightUnit }: { data: MuscleGainData[]; showTitle: TitleStyle | undefined; weightUnit: WeightUnit }) {
  if (data != null && data.length > 0) {
    const hideYear = data[data.length - 1].date.year === DateTime.local().year;
    const predictedDate = formatShortChartDate(_.last(data)!.date, !hideYear);
    const titlePrefix = TitleStyle.Text === showTitle ? i18n.t("splash.charts.titlePrefix") : i18n.t("splash.charts.muscleGain.iconTitle");
    const totalGained = weightUnit === WeightUnit.Pounds ? _.last(data)!.muscleGainedLbs : getWeightKg(_.last(data)!.muscleGainedLbs, WeightUnit.Pounds);
    const titlePrediction = i18n.t("splash.charts.muscleGain.prediction", { amount: totalGained, unit: weightUnit });
    const titlePredictionDate = predictedDate;

    return {
      titlePrefix,
      titlePrediction,
      titlePredictionDate,
    };
  }
  return null;
}

type MuscleGainChartProps = {
  data: MuscleGainData[];
  weightUnit: WeightUnit;
  showTitle?: TitleStyle;
  showOnlyTitle?: boolean;
  showBigHeader?: boolean;
  dedupeMonthTicks?: boolean;
  bendMode?: boolean;
}

const MuscleGainChart = (props: MuscleGainChartProps) => {
  const { data, showTitle, weightUnit, showOnlyTitle, showBigHeader, dedupeMonthTicks, bendMode } = props;
  const { t, i18n: { language } } = useTranslation();

  const predictionData = useMemo(() => {
    return getMuscleGainPredictionData({ data, showTitle, weightUnit });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, showTitle, weightUnit, language]);

  if (predictionData) {
    const { titlePrefix, titlePrediction, titlePredictionDate } = predictionData;

    return <Chart
      data={data.map((d) => ({ ...d, muscleGained: weightUnit === WeightUnit.Kilograms ? getWeightKg(d.muscleGainedLbs, WeightUnit.Pounds) : d.muscleGainedLbs }))}
      dataKey="muscleGained"
      showTitle={showTitle}
      icon={faDumbbell}
      CustomTooltip={createCustomTooltip}
      titlePrefix={titlePrefix}
      titlePrediction={titlePrediction}
      titlePredictionDate={titlePredictionDate}
      finalMarker={`${titlePrediction} ${titlePredictionDate}`}
      caret={'▲'}
      markerLocation="top"
      yAxisLabel={t("splash.charts.muscleGain.yAxis", { unit: weightUnit })}
      showOnlyTitle={showOnlyTitle}
      showBigHeader={showBigHeader}
      dedupeMonthTicks={dedupeMonthTicks}
      bendMode={bendMode}
    />
  } else {
    return <></>;
  }
};

export {
  MuscleGainChart,
  isMuscleGainData,
};

export type { MuscleGainChartProps, MuscleGainData };
